import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "../../../components/Shared/AdminLayout";
import Container from "../../../components/Shared/Container";
import ActionBar from "../../../components/Shared/ActionBar";
import WalletCard from "../../../components/Admin/Wallet/WalletCard";
import WalletEditSidebar from "../../../components/Admin/Wallet/WalletEditSidebar";
import * as adminHandlers from "../redux/handlers";

// Vendor enum matching backend
const VENDOR_NAMES = {
    PINELABS: "PINELABS",
    RAZORPAY: "RAZORPAY",
    VOUCHAGRAM: "VOUCHAGRAM"
};

// Vendor display info
const VENDOR_INFO = {
    PINELABS: {
        name: 'PineLabs',
        logo: '/brand-logo-pinelabs.png'
    },
    RAZORPAY: {
        name: 'Razorpay',
        logo: '/brand-logo-razorpay.jpeg'
    },
    VOUCHAGRAM: {
        name: 'VouchaGram',
        logo: '/brand-logo-vouchagram.png'
    }
};

function Wallet(props) {
    const dispatch = useDispatch();
    const { loading, error, successMessage } = useSelector(state => state.admin);
    const [wallets, setWallets] = useState([]);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
    const [isLoadingWallet, setIsLoadingWallet] = useState(true);

    // Fetch vendor wallet details on mount
    useEffect(() => {
        fetchWalletDetails();
    }, []);

    // Clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                dispatch(adminHandlers.clearSuccessHandler());
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, dispatch]);

    const fetchWalletDetails = async () => {
        setIsLoadingWallet(true);
        try {
            const vendorPromises = Object.keys(VENDOR_NAMES).map(async (vendorKey, index) => {
                await new Promise(resolve => setTimeout(resolve, index * 100));
                try {
                    const response = await dispatch(adminHandlers.fetchVendorDetailsHandler(vendorKey));
                    if (response?.wallet) {
                        const { vendor, balance } = response.wallet;
                        return {
                            id: vendorKey.toLowerCase(),
                            vendor: vendorKey,
                            ...VENDOR_INFO[vendorKey],
                            balance: parseFloat(balance || 0),
                            isEditable: true
                        };
                    }
                    return {
                        id: vendorKey.toLowerCase(),
                        vendor: vendorKey,
                        ...VENDOR_INFO[vendorKey],
                        balance: 0,
                        isEditable: true
                    };
                } catch (err) {
                    console.error(`Failed to fetch ${vendorKey} details:`, err);
                    return {
                        id: vendorKey.toLowerCase(),
                        vendor: vendorKey,
                        ...VENDOR_INFO[vendorKey],
                        balance: 0,
                        isEditable: true
                    };
                }
            });
            
            const walletData = await Promise.all(vendorPromises);
            setWallets(walletData);
        } catch (err) {
            console.error('Failed to fetch wallet details:', err);
            const walletData = Object.keys(VENDOR_NAMES).map(vendorKey => ({
                id: vendorKey.toLowerCase(),
                vendor: vendorKey,
                ...VENDOR_INFO[vendorKey],
                balance: 0,
                isEditable: vendorKey === 'VOUCHAGRAM'
            }));
            setWallets(walletData);
        } finally {
            setIsLoadingWallet(false);
        }
    };

    const handleEditWallet = (wallet) => {
        setSelectedWallet(wallet);
        // Delay opening to allow modal to mount first
        setTimeout(() => {
            setIsEditSidebarOpen(true);
        }, 10);
    };

    const handleSaveBalance = async (walletId, newBalance) => {
        const wallet = wallets.find(w => w.id === walletId);
        if (!wallet) return;

        try {
            const walletData = {
                wallet: {
                    vendor: wallet.vendor,
                    balance: parseFloat(newBalance)
                }
            };

            const response = await dispatch(adminHandlers.updateVendorDetailsHandler(walletData));
            
            if (response?.wallet) {
                // Update local state with new balance
                setWallets(prevWallets =>
                    prevWallets.map(w =>
                        w.vendor === wallet.vendor
                            ? { ...w, balance: response.wallet.balance }
                            : w
                    )
                );
            }

            // Close sidebar after successful save
            setIsEditSidebarOpen(false);
            setSelectedWallet(null);
        } catch (err) {
            console.error('Failed to update wallet balance:', err);
            
            // Check for specific error messages
            if (err?.response?.data?.detail?.message) {
                alert(err.response.data.detail.message);
            } else if (err?.response?.status === 400) {
                alert('Vendor mismatch error. Please refresh and try again.');
            } else if (err?.response?.status === 404) {
                alert('No vendor details found. Please contact support.');
            } else {
                alert('Failed to update wallet balance. Please try again.');
            }
        }
    };

    const getTotalBalance = () => {
        return wallets.reduce((total, wallet) => total + (wallet.balance || 0), 0);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    };

    if (isLoadingWallet) {
        return (
            <AdminLayout>
                <ActionBar title="Vendors Overview" showSearch={false} />
                <Container>
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                </Container>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <ActionBar title="Vendors Overview" showSearch={false} />
            <Container>
                <div className="space-y-6">
                    {/* Vendor Wallets */}
                    <div className="bg-eerie-black border border-zinc-700 rounded-3xl p-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-5 gap-3">
                            <h3 className="text-lg font-semibold text-white">Vendor Wallets</h3>
                            <button
                                onClick={fetchWalletDetails}
                                aria-label="Refresh wallets"
                                className="p-2.5 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="bg-charleston-green border border-zinc-700 rounded-2xl p-5 mb-6">
                            <p className="text-sm text-zinc-400 uppercase mb-1">Total Balance Across All Vendors</p>
                            <p className="text-3xl font-bold text-white">{formatCurrency(getTotalBalance())}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {wallets.map(wallet => (
                                <WalletCard
                                    key={wallet.id}
                                    wallet={wallet}
                                    onEdit={handleEditWallet}
                                    isEditable={wallet.isEditable}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Wallet Distribution */}
                    <div className="bg-eerie-black border border-zinc-700 rounded-3xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Vendor Distribution</h3>
                        <div className="space-y-3">
                            {wallets.map(wallet => {
                                const totalBalance = getTotalBalance();
                                const percentage = totalBalance > 0 ? (wallet.balance / totalBalance) * 100 : 0;
                                return (
                                    <div key={wallet.id} className="flex items-center space-x-4">
                                        <div className="flex items-center space-x-2 w-32">
                                            <img 
                                                src={wallet.logo} 
                                                alt={wallet.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-sm text-zinc-300">
                                                {wallet.name}
                                            </span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="bg-charleston-green rounded-full h-2 overflow-hidden">
                                                <div 
                                                    className="h-full rounded-full transition-all duration-300"
                                                    style={{ 
                                                        width: `${percentage}%`, 
                                                        backgroundColor: '#4B5563' 
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <span className="text-sm text-zinc-400 text-right">
                                            {formatCurrency(wallet.balance)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Container>

            {/* Edit Sidebar */}
            {selectedWallet && (
                <WalletEditSidebar
                    wallet={selectedWallet}
                    isOpen={isEditSidebarOpen}
                    onClose={() => {
                        setIsEditSidebarOpen(false);
                        // Delay clearing selectedWallet to allow close animation
                        setTimeout(() => {
                            setSelectedWallet(null);
                        }, 300);
                    }}
                    onSave={handleSaveBalance}
                />
            )}
        </AdminLayout>
    );
}

export default Wallet;